import { Address } from '../service/AddressHelper/AddressHelper';

import {
    BannerClientInfo,
    EditedBanner,
    EditedBannerCreative,
    EditedBannerTurbolanding,
    EditedBannerVideo,
    GroupBanners,
    MassActionId,
    PromocodesRestrictedDomains,
    ProtocolValue
} from './bannersEditState';
import { DirectInputTextField, EditorPlaceId } from './common';
import {
    BannerImageFormat,
    BannerImageType,
    CampaignId,
    CampaignPlatform,
    CampaignType,
    GroupType
} from './domain';
import {
    adConstantsFragment,
    calloutsConstantsFragment,
    GdAdGroupPrimaryStatus,
    GdAdImageNamespace,
    GdAdPriceCurrency,
    GdCalloutStatusModerate,
    ValidationError,
    ValidationWarning,
    vcardConstantsFragment
} from './graphql-grid';
import { ImageEditorsState } from './state';
import { Omit } from './utils';

export interface Callout {
    clientId?: string;
    id: string;
    text: string;
    flags?: (string | null)[] | null;
    statusModerate: GdCalloutStatusModerate;
}

export interface ImageItem {
    url: string;
    avatarsHost: string;
    height: number;
    hash: string;
    mdsGroupId: string;
    name: string;
    namespace: GdAdImageNamespace;
    type: BannerImageType;
    width: number;
    formats?: Array<BannerImageFormat>;
}

export type TurbolandingId = string;

export enum PrefixPrice {
    DEFAULT = '',
    FROM = 'FROM',
    TO = 'TO'
}

export enum ImageGalleryErrorsTypes {
    INTERNAL_ERROR = 'INTERNAL_ERROR',
    IMAGES_COUNT_NULL = 'IMAGES_COUNT_NULL'
}

export interface SiteLinkData {
    title: string;
    isMassActionsTitleDifferent?: boolean;
    descr: string;
    isMassActionsDescrDifferent?: boolean;
    link: string;
    isMassActionsLinkDifferent?: boolean;
    protocol: ProtocolValue;
    turbo?: TurbolandingPage;
    isMassActionsTurboDifferent?: boolean;
    isTurboLoading: boolean;
    isUrlChecking: boolean;
    isUrlValid: boolean;
    isPreparedSiteLinkFromMassAction?: boolean;
}

export const bannerPreviewSizes = [
    {
        width: 425,
        height: 132
    },
    {
        width: 728,
        height: 90
    },
    {
        width: 400,
        height: 180
    },
    {
        width: 290,
        height: 50
    },
    {
        width: 590,
        height: 50
    },
    {
        width: 300,
        height: 250
    },
    {
        width: 240,
        height: 400
    },
    {
        width: 320,
        height: 480
    }
];

export interface SiteLinkInfo {
    title: SiteLinkTitleField;
    // isMassActionTitleValuesDifferent?: boolean;
    link: SiteLinkHrefField;
    // isMassActionLinkValuesDifferent?: boolean;
    descr: SiteLinkDescrField;
    // isMassActionDescrValuesDifferent?: boolean;
    turbo: SiteLinkTurboField;
    isMassActionTurboValuesDifferent?: boolean;
    protocol: ProtocolValue;
    isMassActionProtocolValuesDifferent?: boolean;
    isPreparedSiteLinkFromMassAction?: boolean;
}

export interface SiteLinkTitleField extends DirectInputTextField {
    isMassActionValuesDifferent?: boolean;
    countFirstBlock: number;
    countSecondBlock?: number;
    limit: number;
}

export interface SiteLinkDescrField extends DirectInputTextField {
    isMassActionValuesDifferent?: boolean;
}

export interface SiteLinkHrefField extends DirectInputTextField {
    isUrlChecking: boolean;
    isMassActionValuesDifferent?: boolean;
}

export type SiteLinkTurboField = {
    data: TurbolandingPage;
    isLoading: boolean;
    isMassActionValuesDifferent?: boolean;
};

export interface BannersBaseInfo {
    mainTitle: DirectInputTextField;
    additionalTitle: DirectInputTextField;
    description: DirectInputTextField;
}

export interface BannerPrice {
    price: string;
    priceOld: string;
    prefix: PrefixPrice;
    currency: GdAdPriceCurrency;
}

export interface BannerPriceForDirect {
    price: DirectInputTextField;
    priceOld: DirectInputTextField;
    prefix: PrefixPrice;
    currency: GdAdPriceCurrency;
}

export type ImageEditorId = BannerId | MassActionId;

export type BannerId = string;
export type CreativeId = string;

export type CalloutsEditorId = BannerId | MassActionId;

export interface TurbolandingPage {
    landingId: TurbolandingId;
    name: string;
    href: string;
}

export interface GroupInfoForBannerCreation {
    name: string;
    id: string;
    adsCount: number;
    canEdit: boolean;
    status: GdAdGroupPrimaryStatus;
    type: GroupType;
}

export interface CampaignInfoForBannerCreation {
    id: string;
    name: string;
    type: CampaignType;
    platform: CampaignPlatform;
}

export interface BannersEditBaseInfoData {
    group?: GroupInfoForBannerCreation;
    campaign?: CampaignInfoForBannerCreation;
    constants: BannerConstants;
    clientId: string;
    callouts: Callout[];
    clientInfo: BannerClientInfo;
    promocodes: PromocodesRestrictedDomains;
    vCardId?: string;
    vCardIds: Record<CampaignId, string | undefined>;
    video?: EditedBannerVideo;
}

export interface BannersEditApiData {
    banners: Record<string, EditedBanner>;
    video: Record<BannerId, EditedBannerVideo>;
    turbolandings: Record<BannerId, EditedBannerTurbolanding>;
    creative: Record<EditorPlaceId, EditedBannerCreative>;
    vCardIds: Record<CampaignId, string | undefined>;
    constants: BannerConstants;
    clientId: string;
    groupBannersList: Record<string, GroupBanners>;
    callouts: Callout[];
    clientInfo: BannerClientInfo;
    promocodes: PromocodesRestrictedDomains;
}

export type BannersEditData = Omit<BannersEditApiData, 'callouts'>;

export interface BannersAndImageEditData {
    bannersEditData: BannersEditApiData;
    imageEditData: ImageEditorsState;
    reqId: string;
}

export enum WeekDay {
    mo = 'mo',
    tu = 'tu',
    we = 'we',
    th = 'th',
    fr = 'fr',
    sa = 'sa',
    su = 'su'
}

export type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type WeekInfo = Array<Day>;

export interface WorkTimeInfo {
    start: string;
    end: string;
}

export type VCardValue =
    | string
    | PointInfo
    | Array<WorkPeriodInfo>
    | PhoneNumberData
    | Address;

export interface WorkPeriodInfo {
    week: WeekInfo;
    time: WorkTimeInfo;
}

export interface VcardData {
    campaignId?: string;
    name: string;
    contactPerson: string | null;
    ogrn: string;
    phone: PhoneNumberData;
    geoId: number;
    mail: string;
    office: string;
    descr: string;
    address: Address;
    messengerType: MessengerType;
    messengerPhone: string;
    pointOnMap: PointInfo | null;
    periods: Array<WorkPeriodInfo>;
    metroStationId: string | null;
    metroStationName: string | null;
}

export enum VcardStatus {
    PROGRESS = 'PROGRESS',
    DISABLED = 'DISABLED',
    EDITING = 'EDITING'
}

export interface PointInfo {
    x: number;
    y: number;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export interface PhoneNumberData {
    codeCountry: string;
    codeRegion: string;
    phoneNumber: string;
    extension: string;
}

export interface MetroStationInfo {
    text: string;
    val: string;
}

export interface AddressField {
    value: Address;
    valid: boolean;
}

export enum MessengerType {
    // TELEGRAM = 'TELEGRAM',
    // WHATSAPP = 'WHATSAPP',
    // VIBER = 'VIBER', // TODO DIRECT-89047 добавить поддеркжу в старом интерфейсе
    ICQ = 'ICQ',
    JABBER = 'JABBER',
    MAIL_AGENT = 'MAIL_AGENT',
    SKYPE = 'SKYPE',
    DEFAULT = 'DEFAULT'
}

export enum CityWithMetroStations {
    MOSCOW = 'MOSCOW',
    KYIV = 'KYIV',
    SPB = 'SAINT_PETERSBURG'
}

export type SitelinksConstants = {
    allowSitelinkLetters: string;
    maxLengthSitelinkTitle: number;
    maxLengthSitelinkHref: number;
    maxLengthSitelinkDescr: number;
    maxLengthSitelinksTitle: number;
};

export interface CanvasConstants {
    canvasDomain: string;
    turbolandingUnifiedApiUrl: string;
}

export type BannersBaseConstants = Pick<
    adConstantsFragment,
    | 'maxLengthTitle'
    | 'maxLengthTitleWord'
    | 'maxLengthTitleExtension'
    | 'maxLengthBody'
    | 'maxLengthBodyWord'
    | 'narrowSymbols'
    | 'maxLengthHref'
    | 'maxLengthTurboGalleryHref'
    | 'maxLengthDisplayHref'
    | 'allowAdLetters'
    | 'maxNumberOfNarrowCharacters'
    | 'aggregationDomains'
>;

export type VCardConstants = Pick<
    vcardConstantsFragment,
    | 'companyNameMaxLength'
    | 'contactPersonMaxLength'
    | 'contactEmailMaxLength'
    | 'extraMessageMaxLength'
    | 'countryMaxLength'
    | 'cityMaxLength'
    | 'streetMaxLength'
    | 'houseWithBuildingMaxLength'
    | 'apartmentMaxLength'
    | 'instantMessengerLoginMaxLength'
    | 'countryCodeMaxLength'
    | 'cityCodeMaxLength'
    | 'phoneNumberMinLength'
    | 'phoneNumberMaxLength'
    | 'extensionMaxLength'
    | 'entirePhoneMinLength'
    | 'entirePhoneMaxLength'
    | 'entirePhoneWithExtensionMaxLength'
>;

export type CalloutsConstants = Pick<
    calloutsConstantsFragment,
    | 'calloutLettersAllowed'
    | 'calloutTextMaxLength'
    | 'calloutsOnClientMaxCount'
    | 'calloutsOnClientMaxCountWithDeleted'
> & {
    calloutsOnBannerMaxNumber: number;
    calloutsStringMaxLength: number;
    showCalloutsMaxNumber: number;
};

export type BannerConstants = {
    ad: BannersBaseConstants;
    vcard: VCardConstants;
    callouts: CalloutsConstants;
    sitelinks: SitelinksConstants;
    canvas: CanvasConstants;
};

export type DeleteBannersResultData = {
    ids: BannerId[];
    errors: ValidationError[];
    warnings?: ValidationWarning[];
};

export type SaveBannersResultData = {
    banners: Array<{ id: BannerId } | null>;
    ids: BannerId[];
    errors: ValidationError[];
    warnings?: ValidationWarning[];
};

export type SaveBannersResult = {
    updated: SaveBannersResultData;
    added: SaveBannersResultData;
    smartAdded: SaveBannersResultData;
    smartUpdated: SaveBannersResultData;
    cpmAdded: SaveBannersResultData;
    cpmUpdated: SaveBannersResultData;
    mcBannerAdded: SaveBannersResultData;
    mcBannerUpdated: SaveBannersResultData;
    deleted: DeleteBannersResultData;
};

export type SaveInternalBannersResult = {
    updated: SaveBannersResultData;
    added: SaveBannersResultData;
    deleted: DeleteBannersResultData;
};

export enum BannerType {
    TEXT = 'text',
    IMAGE = 'image',
    VIDEO = 'video'
}

export interface BannerPriceParams {
    bannerId: string;
    bannerPrice: BannerPrice;
}

export enum BannersScreenUrlSearchQueryParams {
    campaignsIds = 'campaigns-ids',
    groupsIds = 'groups-ids',
    bannersIds = 'banners-ids',
    isNew = 'is-new',
    ulogin = 'ulogin'
}

export type BannerSiteLinksId = BannerId | MassActionId;

export enum ImageOrCreativeUploadingMode {
    EMPTY = 'empty',
    ENTER_LINK = 'enter-link',
    UPLOADING = 'uploading',
    UPLOADING_LOCAL = 'uploading_local',
    UPLOADED = 'uploaded',
    ERROR = 'error'
}

export enum ImageCreativeEditorActionTypes {
    creatives = 'creatives',
    adaptive = 'adaptive',
    local = 'local',
    link = 'link',
    gallery = 'gallery',
    ideas = 'ideas',
    presets = 'presets'
}

export interface BannerForClearOptions {
    keepBannerImageSize: boolean;
}

export type BannersToClear = Record<BannerId, BannerForClearOptions>;
